// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, Loader2, Plug, Search, AlertCircle, Lock, User, SlidersHorizontal,
  Mail, Calendar, Folder, Code2, MessageCircle, Users, BarChart3, CreditCard, ListChecks, PenTool,
} from 'lucide-react';

import { PROVIDER_CATEGORIES } from './providerCatalog';
import { Toggle } from '../../pages/aai/dashboards/ui';
import { aaiApi } from '../../services/aaiApi';
import type { ConnectorCatalog, ConnectorRow, ConnectorScope } from '../../types/aai';
import { ManageInWorkspace, workspaceGitLine } from '../settings/ManageInWorkspace';

/**
 * ConnectorsCatalog — Settings → Connectors.
 *
 * Reads the connector API, which is the single source of connector definitions,
 * authorization and connection ownership for both this app and the builder.
 *
 * What this screen can and cannot show:
 *  - YOUR personal connection per app. Never a colleague's: the API only ever
 *    returns the caller's own personal row plus the organization's shared one.
 *  - The organization's shared connection, when one has been configured.
 *  - The organization's allow-list and the sharing controls, but only to a user
 *    who holds the manage permission (the API enforces this too — this is not
 *    frontend hiding).
 */

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  mail: <Mail className="h-4 w-4" />,
  calendar: <Calendar className="h-4 w-4" />,
  folder: <Folder className="h-4 w-4" />,
  code: <Code2 className="h-4 w-4" />,
  chat: <MessageCircle className="h-4 w-4" />,
  crm: <Users className="h-4 w-4" />,
  analytics: <BarChart3 className="h-4 w-4" />,
  payments: <CreditCard className="h-4 w-4" />,
  tasks: <ListChecks className="h-4 w-4" />,
  design: <PenTool className="h-4 w-4" />,
};

const VISUALS: Record<string, { color: string; textColor?: string; initial: string; iconKey?: string }> = {};
for (const cat of PROVIDER_CATEGORIES) {
  for (const p of cat.providers) {
    VISUALS[p.slug] = { color: p.color, textColor: p.textColor, initial: p.initial, iconKey: cat.iconKey };
  }
}

const isLive = (status: string | null): boolean => {
  const s = String(status || '').toLowerCase();
  return s === 'connected' || s === 'active';
};

const ConnectorsCatalog: React.FC<{
  clientId?: string;
  onConnected?: () => void;
  /** Owner only: opens what an assistant may do in this tool, for the whole company. */
  onPermissions?: (row: ConnectorRow) => void;
}> = ({ onConnected, onPermissions }) => {
  const [data, setData] = useState<ConnectorCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const pollRef = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await aaiApi.getConnectors();
      setData(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load your connectors.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  /**
   * Wait for the grant to land. Composio can report ACTIVE a moment AFTER the
   * popup closes, so a single check right on close was reading "not connected"
   * for a connection that had in fact succeeded — the row stayed 'pending' and
   * the tile still offered Connect. Each attempt is an authenticated status
   * read, which is also what records the connection server-side.
   */
  const confirm = async (row: ConnectorRow, scope: ConnectorScope): Promise<boolean> => {
    for (let i = 0; i < 8; i++) {
      try {
        const { data: st } = await aaiApi.getConnectorStatus(row.slug, scope);
        if (st.connected) return true;
      } catch { /* keep trying — the popup may still be finishing */ }
      await new Promise((r) => setTimeout(r, 1300));
    }
    return false;
  };

  const connect = async (row: ConnectorRow, scope: ConnectorScope) => {
    const key = `${row.slug}:${scope}`;
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const { data: res } = await aaiApi.initiateConnector(row.slug, scope);
      if (res.alreadyConnected) {
        // Not a no-op that failed: this account was already authorized (often
        // from a tool dive's own connect step), so there is nothing to consent
        // to. Say so, or it reads as "it connected without asking me".
        setNotice(`${row.label} was already authorized for ${scope === 'organization' ? 'your organization' : 'your account'}, so there was nothing to approve.`);
        setBusy(null);
        await load();
        onConnected?.();
        return;
      }
      if (!res.redirectUrl) {
        setError(`Couldn't start the ${row.label} connection — try again in a moment.`);
        setBusy(null);
        return;
      }
      const popup = window.open(
        res.redirectUrl, `connect-${row.slug}`,
        'width=600,height=720,menubar=no,toolbar=no,location=no,status=no',
      );
      if (!popup || popup.closed) { window.location.href = res.redirectUrl; return; }
      const started = Date.now();
      pollRef.current = setInterval(async () => {
        if (popup.closed) {
          clearInterval(pollRef.current);
          const ok = await confirm(row, scope);
          await load();
          setBusy(null);
          if (ok) {
            onConnected?.();
          } else {
            setError(`${row.label} didn't finish connecting. If you closed the window early, try again.`);
          }
        } else if (Date.now() - started > 10 * 60 * 1000) {
          clearInterval(pollRef.current);
          setBusy(null);
        }
      }, 900);
    } catch (e: any) {
      setError(e?.response?.data?.detail || `Couldn't start the ${row.label} connection.`);
      setBusy(null);
    }
  };

  const disconnect = async (row: ConnectorRow, scope: ConnectorScope) => {
    const which = scope === 'organization' ? `the organization's ${row.label}` : `your ${row.label}`;
    if (!window.confirm(`Disconnect ${which}? Anything relying on it stops working until it is reconnected.`)) return;
    setBusy(`${row.slug}:${scope}`);
    setError(null);
    try {
      await aaiApi.revokeConnector(row.slug, scope);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || `Couldn't disconnect ${row.label}.`);
    } finally {
      setBusy(null);
    }
  };

  const setEnabled = async (row: ConnectorRow, enabled: boolean) => {
    // Switching a connector off takes it away from every colleague, including
    // ones already using it, so it is worth one question first. Switching back
    // on takes nothing away and asks nothing.
    if (!enabled) {
      const ok = window.confirm(
        `Turn off ${row.label} for everyone at your organization?\n\n`
        + `Nobody will be able to connect it, and anyone already using it stops being able to. `
        + `You can turn it back on at any time and their connections return.`,
      );
      if (!ok) return;
    }
    setBusy(`${row.slug}:policy`);
    setError(null);
    try {
      await aaiApi.setConnectorEnabled(row.slug, enabled);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || `Couldn't update ${row.label}.`);
    } finally {
      setBusy(null);
    }
  };

  const grouped = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    const byCat: Record<string, ConnectorRow[]> = {};
    for (const row of data.connectors) {
      // MCP servers have their own tab.
      if (row.kind === 'mcp') continue;
      if (q && !row.label.toLowerCase().includes(q) && !row.slug.includes(q)
          && !row.category_label.toLowerCase().includes(q)) continue;
      (byCat[row.category] = byCat[row.category] || []).push(row);
    }
    return data.categories
      .map((c) => ({ ...c, rows: byCat[c.id] || [] }))
      .filter((c) => c.rows.length > 0);
  }, [data, query]);

  const myCount = useMemo(
    () => (data?.connectors || []).filter((c) => isLive(c.personal_status)).length,
    [data],
  );

  if (loading) {
    return (
      <div className="grid place-items-center py-14">
        <Loader2 className="h-5 w-5 animate-spin text-ink-soft" />
      </div>
    );
  }

  // No connector permission: the feature does not exist for this user. The API
  // returns an empty catalog for them too, so this is not the only gate.
  if (data && !data.can_use && !data.can_manage) {
    return (
      <div className="rounded-panel border border-line bg-white px-5 py-12 text-center">
        <Lock className="mx-auto mb-3 h-6 w-6 text-ink-soft" />
        <div className="text-sm font-semibold text-ink">Connectors aren't part of your access.</div>
        <p className="mx-auto mt-1 max-w-md text-[13px] text-ink-dim">
          Ask whoever manages your workspace to give you connector access if you need to connect
          your own tools.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-sans text-[10.5px] font-semibold uppercase tracking-[0.09em] text-brand-600">
            Your tools{myCount > 0 ? ` · ${myCount} connected` : ''}
          </div>
          <p className="mt-1 max-w-xl text-[13px] text-ink-dim">
            What you connect here is yours. Your teammates keep their own connections.
          </p>
        </div>
        <div className="relative w-[220px] max-w-full">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search apps"
            className="h-9 w-full rounded-lg border border-line bg-white pl-8 pr-2.5 text-[12.5px] text-ink outline-none placeholder:text-ink-soft focus:border-ink focus:ring-2 focus:ring-ink/15"
          />
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <div className="flex-1 text-[12.5px] leading-snug text-red-800">{error}</div>
          <button type="button" onClick={() => setError(null)} className="shrink-0 text-[11.5px] text-red-400 hover:text-red-700">
            Dismiss
          </button>
        </div>
      )}

      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" strokeWidth={3} />
          <div className="flex-1 text-[12.5px] leading-snug text-brand-800">{notice}</div>
          <button type="button" onClick={() => setNotice(null)} className="shrink-0 text-[11.5px] text-brand-400 hover:text-brand-700">
            Dismiss
          </button>
        </div>
      )}

      {grouped.length === 0 ? (
        <div className="rounded-panel border border-line bg-white px-5 py-14 text-center">
          <Search className="mx-auto mb-3 h-7 w-7 text-ink-soft" />
          <div className="text-sm font-semibold text-ink">No apps match.</div>
          <p className="mt-1 text-sm text-ink-dim">Try a different name.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map((cat) => (
            <div key={cat.id}>
              <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-ink-muted">
                <span className="text-ink-soft">{CATEGORY_ICON[VISUALS[cat.rows[0].slug]?.iconKey || '']}</span>
                {cat.label}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {cat.rows.map((row) => {
                  const v = VISUALS[row.slug] || { color: '#4F46E5', initial: row.label.charAt(0) };
                  const mine = isLive(row.personal_status);
                  const connectBusy = busy === `${row.slug}:personal`;
                  return (
                    <div
                      key={row.slug}
                      className={`rounded-card border bg-white px-3 py-2.5 ${row.enabled ? 'border-line' : 'border-line-soft bg-canvas/60'}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span
                          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[12px] font-bold"
                          style={{ backgroundColor: v.color, color: v.textColor || '#fff' }}
                        >
                          {v.initial}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-medium text-ink">{row.label}</span>
                          {!row.enabled && !data.can_manage && (
                            <span className="text-[11px] text-ink-soft">
                              Not available at your organization
                            </span>
                          )}
                        </div>
                        {data.can_manage && !data.can_switch_company && (
                          <span className="shrink-0 text-right text-[11.5px] font-medium text-ink-soft">
                            {row.enabled ? 'Allowed for everyone' : 'Turned off for everyone'}
                            <span className="block font-normal">Ask the owner to change this</span>
                          </span>
                        )}
                        {data.can_switch_company && row.connect_here && (
                          // Spelled out, because "should my company be allowed to
                          // use this tool at all?" is not a question a bare power
                          // icon asks legibly. Code hosts carry no switch: they
                          // are connected once in the Workspace, for the company.
                          <span className="flex shrink-0 items-center gap-2">
                            <span className={`text-[11.5px] font-medium ${row.enabled ? 'text-ink-dim' : 'text-ink-soft'}`}>
                              {row.enabled ? 'Allowed for everyone' : 'Turned off for everyone'}
                            </span>
                            <Toggle
                              on={row.enabled}
                              onChange={(next) => setEnabled(row, next)}
                              disabled={busy === `${row.slug}:policy`}
                            />
                          </span>
                        )}
                      </div>
                      {data.can_manage && !row.enabled && row.connect_here && (
                        <p className="mt-1.5 text-[11.5px] text-ink-dim">
                          Nobody at your organization can connect {row.label} while this is off.
                        </p>
                      )}

                      {/* Code hosting is connected once for the company in the
                          Workspace, which owns that credential. */}
                      {row.enabled && !row.connect_here && (
                        <div className="mt-2 flex items-center justify-between gap-3 border-t border-line-soft pt-2">
                          <p className="m-0 text-[11.5px] text-ink-dim">{workspaceGitLine(row)}</p>
                          <ManageInWorkspace row={row} size="xs" />
                        </div>
                      )}
                      {row.enabled && row.connect_here && (
                        <div className="mt-2 space-y-1 border-t border-line-soft pt-2">
                          {/* Personal — always the caller's own account. */}
                          {data.can_use && (
                            <div className="flex items-center gap-2">
                              <User className="h-3 w-3 shrink-0 text-ink-soft" />
                              <span className="text-[11.5px] text-ink-dim">Personal</span>
                              <span className="flex-1" />
                              {mine ? (
                                <>
                                  <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-emerald-600">
                                    <Check className="h-3.5 w-3.5" strokeWidth={3} /> Yours
                                  </span>
                                  <button
                                    type="button" onClick={() => disconnect(row, 'personal')} disabled={connectBusy}
                                    className="text-[11px] text-ink-soft hover:text-red-600 disabled:opacity-50"
                                  >
                                    Disconnect
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button" onClick={() => connect(row, 'personal')} disabled={connectBusy}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2 py-1 text-[11.5px] font-semibold text-ink-muted transition-all hover:border-brand-300 hover:text-brand-700 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {connectBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
                                  {connectBusy ? 'Connecting…' : 'Connect'}
                                </button>
                              )}
                            </div>
                          )}

                          {onPermissions && data.can_manage && (
                            <div className="flex items-center gap-2">
                              <SlidersHorizontal className="h-3 w-3 shrink-0 text-ink-soft" />
                              <span className="text-[11.5px] text-ink-dim">Permissions</span>
                              <span className="flex-1" />
                              <button
                                type="button" onClick={() => onPermissions(row)}
                                className="text-[11.5px] font-semibold text-ink-muted underline-offset-2 hover:text-ink hover:underline"
                              >
                                What an assistant may do
                              </button>
                            </div>
                          )}
                          {/* Organization connections are off for now: every person connects
                              their own apps. The shared row below comes back when that changes.
                          {(shared || data.can_manage) && (
                          <div className="flex items-center gap-2">
                          <Building2 className="h-3 w-3 shrink-0 text-ink-soft" />
                          <span className="text-[11.5px] text-ink-dim">Organization</span>
                          <span className="flex-1" />
                          {shared ? (
                          <>
                          <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-brand-600">
                          <Check className="h-3.5 w-3.5" strokeWidth={3} /> Shared
                          </span>
                          {data.can_manage && (
                          <button
                          type="button" onClick={() => disconnect(row, 'organization')} disabled={orgBusy}
                          className="text-[11px] text-ink-soft hover:text-red-600 disabled:opacity-50"
                          >
                          Remove
                          </button>
                          )}
                          </>
                          ) : (
                          <button
                          type="button" onClick={() => connect(row, 'organization')} disabled={orgBusy}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2 py-1 text-[11.5px] font-semibold text-ink-muted transition-all hover:border-brand-300 hover:text-brand-700 disabled:opacity-50 whitespace-nowrap"
                          >
                          {orgBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Building2 className="h-3 w-3" />}
                          {orgBusy ? 'Connecting…' : 'Share one'}
                          </button>
                          )}
                          </div>
                          )}
                          */}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConnectorsCatalog;
